import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { reduxForm, Field, formValues } from 'redux-form'
import { cx } from '@emotion/css'

import { SingleSearch, ButtonLink, FormErrorNotification, FormLabel, requiredField, SubmitButton } from '@offerzen/pinata'

import { MonthYearPicker } from '@offerzen/pinata/datepickers'

import styles from './styles'
import { formatEducationInput } from '../../../../CandidateExperience/helpers/educationInputHelpers'

const validators = {
  requiredSearch: value => {
    if (!value.label) return 'Required'

    return undefined
  },
  required: requiredField('Required'),
}

const EducationItemForm = ({
  isNew,
  touch,
  handleSubmit,
  error,
  form,
  searchingUniversities,
  searchUniversities,
  universitiesSearchResults,
  searchingDegrees,
  searchDegrees,
  degreesSearchResults,
  startYear,
  graduationYear,
  submitting,
  toggleEditing,
  deleteBlankEducation,
}) => {
  useEffect(() => {
    // Trigger validations if there is any missing data.
    // ReduxForm doesn't have a better way to do this.
    // `touch()` should be a dependency of the effect, but it's value is not stable due to ReduxForm.
    if (!isNew) touch('university', 'degree', 'start_year', 'graduation_year')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew])

  const startField = useRef(null)
  const endField = useRef(null)

  return (
    <form onSubmit={handleSubmit} className={styles.form} data-test="education-form">
      <If condition={error}>
        <FormErrorNotification text={error} />
      </If>

      <div className={styles.fieldSet}>
        <div>
          <FormLabel htmlFor={`${form}-university`} label="Institution name" />
          <Field
            id={`${form}-university`}
            name="university"
            className={cx(styles.fieldInput, styles.searchInput)}
            theme="primary"
            placeholder="Enter an institution name"
            fetching={searchingUniversities}
            onSearch={searchUniversities}
            results={universitiesSearchResults}
            validate={validators.requiredSearch}
            component={SingleSearch}
            autoFocus
          />
        </div>

        <div>
          <FormLabel htmlFor={`${form}-degree`} label="Qualification" />
          <Field
            id={`${form}-degree`}
            name="degree"
            className={cx(styles.fieldInput, styles.searchInput)}
            theme="primary"
            placeholder="Enter a qualification name"
            fetching={searchingDegrees}
            onSearch={searchDegrees}
            results={degreesSearchResults}
            validate={validators.requiredSearch}
            component={SingleSearch}
          />
        </div>
      </div>

      <div className={styles.fieldSet}>
        <div>
          <FormLabel htmlFor={`${form}-start-year`} label="Start date" />
          <Field
            id={`${form}-start-year`}
            name="start_year"
            innerRef={el => {
              startField.current = el
            }}
            placeholder="Select start date"
            maxDate={graduationYear}
            theme="primary"
            validate={validators.required}
            onChange={() => {
              if (!graduationYear && startField.current && endField.current) {
                startField.current.click()
              }
            }}
            onBlur={ev => {
              // HACK: bandaid fix for a bug where MonthYearPicker changes the Date format to a format incompatible with Firefox
              ev.preventDefault()
            }}
            component={MonthYearPicker}
            className={styles.fieldInput}
          />
        </div>

        <div>
          <FormLabel htmlFor={`${form}-graduation-year`} label="Graduation date" />
          <Field
            id={`${form}-graduation-year`}
            name="graduation_year"
            innerRef={el => {
              endField.current = el
            }}
            placeholder="Select graduation date"
            minDate={startYear}
            theme="primary"
            component={MonthYearPicker}
            onBlur={ev => {
              // HACK: bandaid fix for a bug where MonthYearPicker changes the Date format to a format incompatible with Firefox
              ev.preventDefault()
            }}
            className={styles.fieldInput}
            validate={validators.required}
          />
        </div>
      </div>

      <div className={styles.buttons}>
        <ButtonLink
          className={styles.cancel}
          size="small"
          label="Cancel"
          theme="light"
          onClick={() => {
            if (isNew) deleteBlankEducation()
            else toggleEditing(false)
          }}
        />
        <SubmitButton label="Save" submittingLabel="Saving" isSubmitting={submitting} theme="primary" variant="outlined" />
      </div>
    </form>
  )
}

EducationItemForm.propTypes = {
  form: PropTypes.string.isRequired,
  isNew: PropTypes.bool,
  touch: PropTypes.func,
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  searchingUniversities: PropTypes.bool,
  searchUniversities: PropTypes.func,
  universitiesSearchResults: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  searchingDegrees: PropTypes.bool,
  searchDegrees: PropTypes.func,
  degreesSearchResults: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  startYear: PropTypes.instanceOf(Date),
  graduationYear: PropTypes.instanceOf(Date),
  submitting: PropTypes.bool,
  toggleEditing: PropTypes.func,
  deleteBlankEducation: PropTypes.func,
}

export default reduxForm({
  onSubmit: (values, _, props) =>
    new Promise((resolve, reject) => {
      const formattedInput = formatEducationInput(values, props.initialValues)

      if (props.isNew) props.createEducation(formattedInput, resolve, reject)
      else props.updateEducation(formattedInput, resolve, reject)
    }),
  onSubmitSuccess: (_, __, props) => {
    if (props.isNew) props.deleteBlankEducation()
    else props.toggleEditing(false)
  },
  touchOnBlur: false,
})(
  formValues({
    graduationYear: 'graduation_year',
    startYear: 'start_year',
  })(EducationItemForm),
)
